import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { ShoppingCart, Truck, Package, ChevronLeft, Plus, Minus } from "lucide-react";
import { useCart } from "../context/CartContext";

export const ProductDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setQty(1);
    });
  }, [id]);

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20">Loading…</div>;

  const unitPrice = bulk ? product.wholesale_price : product.price;
  const total = unitPrice * qty;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-indigo-600 mb-6">
        <ChevronLeft className="w-4 h-4" /> Back to shop
      </Link>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="rounded-3xl overflow-hidden bg-slate-100 aspect-square">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-detail-image" />
        </div>
        <div>
          {product.featured && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 mb-2">Featured</Badge>}
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold" data-testid="product-detail-name">{product.name}</h1>
          <div className="mt-2 text-slate-600">{product.description}</div>

          <div className="mt-6 p-5 rounded-2xl bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Buying for a class or event?</div>
                <div className="font-semibold text-sm">Bulk pricing (min {product.bulk_min_qty})</div>
              </div>
              <Switch checked={bulk} onCheckedChange={(v) => { setBulk(v); if (v && qty < product.bulk_min_qty) setQty(product.bulk_min_qty); }} data-testid="product-bulk-buy-toggle" />
            </div>
            <div className="flex items-center gap-6">
              <div>
                <div className="text-3xl font-display font-extrabold" data-testid="product-detail-price">${unitPrice.toFixed(2)}</div>
                <div className="text-xs text-slate-500">{bulk ? "Wholesale unit price" : "Retail unit price"}</div>
              </div>
              <div className="flex items-center gap-2 border border-slate-200 rounded-full bg-white p-1">
                <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => setQty(Math.max(bulk ? product.bulk_min_qty : 1, qty - 1))} data-testid="qty-decrease"><Minus className="w-4 h-4" /></Button>
                <div className="w-10 text-center font-semibold" data-testid="product-qty">{qty}</div>
                <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => setQty(qty + 1)} data-testid="qty-increase"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="text-sm">
                <div className="text-slate-500">Total</div>
                <div className="font-display font-bold text-lg" data-testid="product-total">${total.toFixed(2)}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <Button size="lg" className="rounded-full bg-indigo-600 hover:bg-indigo-700 flex-1"
                    onClick={() => addItem(product, qty, bulk)} data-testid="product-add-cart-detail">
              <ShoppingCart className="w-4 h-4 mr-2" /> Add {qty} to cart
            </Button>
            <Button size="lg" variant="outline" className="rounded-full"
                    onClick={() => { addItem(product, qty, bulk); nav("/cart"); }} data-testid="product-buy-now">
              Buy now
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2 p-3 bg-white rounded-xl border border-slate-200">
              <Truck className="w-4 h-4 text-indigo-600 mt-0.5" />
              <div><div className="font-semibold">Auto-fulfilled</div><div className="text-xs text-slate-500">Wholesaler handles it live</div></div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-white rounded-xl border border-slate-200">
              <Package className="w-4 h-4 text-emerald-600 mt-0.5" />
              <div><div className="font-semibold">{product.stock} in stock</div><div className="text-xs text-slate-500">Ships within 24h</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
